import type { QueryClient } from '@tanstack/react-query'

import { API_ORIGIN } from '@/constants'
import type { EntitlementDeniedPayload } from '@/components/gating/types'
import { parseEntitlementDeniedFromResponse } from '@/components/gating/parse-entitlement-denied'
import type { OrganisationOpportunity } from '@/hooks/use-organisation-opportunities'
import type { LifecycleAction, PublicationStatus } from '@/utils/opportunity-lifecycle'

type LifecycleOk = {
  publication_status?: string
  public_slug?: string | null
  published_at?: string | null
  detail_url?: string | null
  public_url?: string | null
}

export type LifecycleMutationError = Error & {
  denial?: EntitlementDeniedPayload
}

function patchOpportunityLifecycle(
  queryClient: QueryClient,
  opportunityId: string,
  payload: LifecycleOk,
) {
  const ps = payload.publication_status
  const cachePatched =
    ps === 'draft' || ps === 'published' || ps === 'closed' || ps === 'archived'
  if (!cachePatched) return

  const patchOpportunity = (o: OrganisationOpportunity): OrganisationOpportunity => ({
    ...o,
    publication_status: (ps as OrganisationOpportunity['publication_status']) ?? o.publication_status,
    public_slug: payload.public_slug !== undefined ? payload.public_slug : o.public_slug,
    published_at: payload.published_at !== undefined ? payload.published_at : o.published_at,
  })

  queryClient.setQueriesData<OrganisationOpportunity>(
    { queryKey: ['opportunity', opportunityId], exact: true },
    (old) => (old ? patchOpportunity(old) : old),
  )
  queryClient.setQueriesData<OrganisationOpportunity[]>(
    { queryKey: ['organisation-opportunities'], exact: false },
    (old) => {
      if (!old?.length) return old
      return old.map((o) =>
        String(o.id) === String(opportunityId) ? patchOpportunity(o) : o,
      )
    },
  )
  queryClient.setQueriesData<OrganisationOpportunity>(
    { queryKey: ['organisation-opportunity'], exact: false },
    (old) => {
      if (!old || String(old.id) !== String(opportunityId)) return old
      return patchOpportunity(old)
    },
  )
}

export async function transitionEmployerJobLifecycle(args: {
  token: string | null
  opportunityId: string
  action: LifecycleAction
  queryClient: QueryClient
  onPublished?: (payload: LifecycleOk) => void
}): Promise<LifecycleOk> {
  const { token, opportunityId, action, queryClient, onPublished } = args
  if (!token) {
    throw new Error('You need to be signed in to update job status.')
  }
  const url = `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/lifecycle/`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action }),
  })
  if (!response.ok) {
    let body: unknown = {}
    try {
      body = await response.json()
    } catch {
      // ignore
    }
    const denial = parseEntitlementDeniedFromResponse(response.status, body)
    if (denial) {
      const err = new Error(
        denial.reason || `This action requires a plan upgrade (${denial.feature_key}).`,
      ) as LifecycleMutationError
      err.denial = denial
      throw err
    }
    const errBody = body as { detail?: string; error?: string }
    throw new Error(errBody.detail || errBody.error || `Failed to ${action} opportunity`)
  }
  let payload: LifecycleOk = {}
  try {
    payload = (await response.json()) as LifecycleOk
  } catch {
    // empty body
  }
  if (action === 'publish' && onPublished) {
    onPublished(payload)
  }
  patchOpportunityLifecycle(queryClient, opportunityId, payload)
  await queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] })
  await queryClient.invalidateQueries({ queryKey: ['organisation-opportunities'] })
  await queryClient.invalidateQueries({ queryKey: ['organisation-opportunity'] })
  await queryClient.invalidateQueries({ queryKey: ['opportunities'] })
  await queryClient.invalidateQueries({ queryKey: ['public-jobs'] })
  return payload
}

export function isPublicationStatus(value: string | undefined): value is PublicationStatus {
  return value === 'draft' || value === 'published' || value === 'closed' || value === 'archived'
}
